import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import hospitalStructureService from "../../../Services/Hospitalstructureservice";
import { FaArrowRight } from "react-icons/fa6";
import { FaArrowLeft } from "react-icons/fa";
import { IoChevronDownSharp } from "react-icons/io5";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[600] text-gray-400 mb-3 mt-8 border-b border-gray-100 pb-2">
    {children}
  </p>
);

const InputField = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">
      {label} {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
    </label>
    <input
      {...props}
      className="w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
    />
  </div>
);

const CustomSelect = ({ label, value, onChange, name, options, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <label className="text-[12px] font-[500] text-gray-500">{label}</label>
      <div className="relative">
        <button type="button" onClick={() => !disabled && setOpen((p) => !p)} disabled={disabled}
          className={`w-full px-3 py-[9px] text-[13px] text-left bg-white border rounded-lg outline-none transition-colors flex items-center justify-between
            ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "hover:border-orange-200 cursor-pointer"}
            ${open ? "border-orange-300 ring-2 ring-orange-100" : "border-gray-200"}
            ${value ? "text-gray-800" : "text-gray-400"}`}>
          <span>{value || `Select ${label}`}</span>
          <IoChevronDownSharp size={16} />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[200px] overflow-y-auto">
            {options.map((opt) => {
              const val = typeof opt === "object" ? opt.value : opt;
              const lbl = typeof opt === "object" ? opt.label : opt;
              return (
                <button key={val} type="button"
                  onClick={() => { onChange({ target: { name, value: val } }); setOpen(false); }}
                  className={`w-full px-3 py-[9px] text-[13px] text-left transition-colors hover:bg-gray-50 hover:text-orange-600
                    ${value === val ? "bg-orange-50 text-orange-600 font-[500]" : "text-gray-700"}`}>
                  {lbl}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const PKRField = ({ label, hint, name, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">
      {label} {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
    </label>
    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden hover:border-orange-200 focus-within:border-orange-300 focus-within:ring-2 focus-within:ring-orange-100 transition-colors bg-white">
      <span className="px-3 py-[9px] text-[12px] text-gray-400 font-[500] bg-gray-50 border-r border-gray-200 select-none">PKR</span>
      <input type="number" name={name} value={value} onChange={onChange} placeholder="0" min="0" step="1"
        className="flex-1 px-3 py-[9px] text-[13px] text-gray-800 bg-white outline-none min-w-0" />
    </div>
  </div>
);

const PercentField = ({ label, name, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden hover:border-orange-200 focus-within:border-orange-300 focus-within:ring-2 focus-within:ring-orange-100 transition-colors bg-white">
      <span className="px-3 py-[9px] text-[12px] text-gray-400 font-[500] bg-gray-50 border-r border-gray-200 select-none">%</span>
      <input type="number" name={name} value={value} onChange={onChange} placeholder="0" min="0" max="100" step="1"
        className="flex-1 px-3 py-[9px] text-[13px] text-gray-800 bg-white outline-none min-w-0" />
    </div>
  </div>
);

const ROOM_TYPES = ["General", "Semi-Private", "Private", "VIP", "ICU", "Deluxe", "NICU", "HDU"];
const WARD_NAMES = ["General Male", "General Female", "Surgical", "Maternity", "Pediatric", "Orthopedic", "Cardiac", "Oncology", "Neurology", "Burns"];

const UpdateHospitalStructure = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(null);

  const isRoom = formData?.entry_type === "room";

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        setFetching(true);
        const data = await hospitalStructureService.getByID(id);
        if (data.responseCode === 200) {
          setFormData(data.structure);
        } else {
          toast.error("Record not found.");
          navigate(-1);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load pricing record.");
        navigate(-1);
      } finally {
        setFetching(false);
      }
    };
    fetchRecord();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (isRoom) {
      if (!formData.room_type) e.room_type = "Please select a room type";
      if (!formData.room_number?.trim()) e.room_number = "Room number is required";
    } else {
      if (!formData.ward_name) e.ward_name = "Please select a ward";
      if (!formData.bed_number?.trim()) e.bed_number = "Bed number is required";
    }
    if (!formData.rate_per_day) e.rate_per_day = "Rate per day is required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the highlighted errors.");
      return;
    }
    setLoading(true);
    try {
      const data = await hospitalStructureService.update({
        id: formData.id,
        entry_type: formData.entry_type,
        room_type: formData.room_type || null,
        room_number: formData.room_number || null,
        ward_name: formData.ward_name || null,
        bed_number: formData.bed_number || null,
        rate_per_day: formData.rate_per_day,
        discount_percent: formData.discount_percent || 0,
      });
      if (data.success) {
        toast.success("Pricing updated successfully!");
        navigate(-1);
      } else {
        toast.error(data.message || "Failed to update pricing.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const ErrMsg = ({ field }) =>
    errors[field] ? <span className="text-[11px] text-red-400 mt-0.5 block">{errors[field]}</span> : null;

  if (fetching) {
    return (
      <div className="p-3 sm:p-6">
        <div className="border border-gray-200 bg-white rounded-xl overflow-hidden shadow-sm">
          <div className="bg-[#215187] px-4 sm:px-6 py-5 sm:py-6">
            <h2 className="text-white text-[14px] font-[500] uppercase tracking-widest">Update Pricing</h2>
            <p className="text-[12px] text-gray-400 mt-0.5">Loading record...</p>
          </div>
          <div className="px-4 sm:px-6 py-16 text-center text-[13px] text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="border border-gray-200 bg-white rounded-xl overflow-hidden shadow-sm">

        <div className="bg-[#215187] px-4 sm:px-6 py-5 sm:py-6">
          <h2 className="text-white text-[14px] font-[500] uppercase tracking-widest">Update Pricing</h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {isRoom
              ? `${formData.room_type} · Room ${formData.room_number}`
              : `${formData.ward_name} · Bed ${formData.bed_number}`}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-5 sm:py-6">

          {isRoom && (
            <>
              <SectionLabel>Room Details</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <CustomSelect label="Room Type" name="room_type" value={formData.room_type} onChange={handleChange} options={ROOM_TYPES} disabled />
                  <ErrMsg field="room_type" />
                </div>
                <div>
                  <InputField label="Room Number" name="room_number" value={formData.room_number} onChange={handleChange} placeholder="e.g. 201-A" disabled />
                  <ErrMsg field="room_number" />
                </div>
              </div>
            </>
          )}

          {!isRoom && (
            <>
              <SectionLabel>Ward Details</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <CustomSelect label="Ward Name" name="ward_name" value={formData.ward_name} onChange={handleChange} options={WARD_NAMES} disabled />
                  <ErrMsg field="ward_name" />
                </div>
                <div>
                  <InputField label="Bed Number" name="bed_number" value={formData.bed_number} onChange={handleChange} placeholder="e.g. B-04" disabled />
                  <ErrMsg field="bed_number" />
                </div>
              </div>
            </>
          )}

          <SectionLabel>
            Pricing —{" "}
            <span className="normal-case font-[400]">
              {isRoom
                ? `${formData.room_type} Room ${formData.room_number}`.trim()
                : `${formData.ward_name} · Bed ${formData.bed_number}`}
            </span>
          </SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <PKRField label="Rate Per Day *" name="rate_per_day" value={formData.rate_per_day} onChange={handleChange} />
              <ErrMsg field="rate_per_day" />
            </div>
            <PercentField label="Discount" name="discount_percent" value={formData.discount_percent} onChange={handleChange} />
          </div>

          <div className="border-t border-gray-200 mt-8 pt-5 sm:pt-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto text-center text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] py-2"
            >
              <FaArrowLeft size={12} className="inline mb-0.5 mr-1" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="relative overflow-hidden bg-[#2F6DB4] text-white text-[12px] sm:text-[13px] font-[500] px-3 sm:px-4 py-2 rounded-lg border border-gray-300 shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 before:absolute before:inset-0 before:-translate-x-full hover:before:translate-x-full before:bg-white/20 before:transition-transform before:duration-500 whitespace-nowrap">
            
              {loading ? (
                "Saving..."
              ) : (
                <>
                  Update Pricing <FaArrowRight className="inline " />
                </>
              )}
            </button>
          </div>

        </form>
      </div>
      <p className="text-center text-[12px] text-gray-400 mt-4">Fields marked with * are required.</p>
    </div>
  );
};

export default UpdateHospitalStructure;